<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

/**
 * Computed boolean indicating if the editor is in private note mode
 * When isReplyRestricted is true, force switch to private note
 * Otherwise, respect the current mode prop
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => {
  if (props.isReplyRestricted) {
    // Force switch to private note when replies are restricted
    return true;
  }
  // Otherwise respect the current mode
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  const xTranslate = isPrivate.value ? replyModeSize.width.value + 16 : 0;

  return `${xTranslate}px`;
});
</script>

<template>
  <button
    class="group relative z-0 inline-flex h-9 w-fit shrink-0 items-center rounded-polaris-lg bg-polaris-muted p-[3px] type-polaris-label transition-all duration-300 ease-in-out active:scale-[0.995] active:duration-75 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50"
    :disabled="disabled || isReplyRestricted"
    :class="{
      'cursor-not-allowed opacity-50': disabled || isReplyRestricted,
    }"
    @click="$emit('toggleMode')"
  >
    <!-- The kit's Tabs look (fase 2b: muted track, the active segment raised on the background); still one button that
      toggles the mode, as the woot always did. -->
    <div
      ref="wootEditorReplyMode"
      class="z-20 flex items-center gap-1 whitespace-nowrap px-2"
      :class="
        isPrivate
          ? 'text-polaris-foreground dark:text-polaris-muted-foreground'
          : 'text-polaris-foreground'
      "
    >
      {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
    </div>
    <div
      ref="wootEditorPrivateMode"
      class="z-20 flex items-center gap-1 whitespace-nowrap px-2"
      :class="
        isPrivate
          ? 'text-polaris-foreground'
          : 'text-polaris-foreground dark:text-polaris-muted-foreground'
      "
    >
      {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
    </div>
    <div
      class="absolute h-[calc(100%-6px)] w-[var(--chip-width)] translate-x-[var(--translate-x)] rounded-polaris-md border border-solid border-transparent bg-polaris-background shadow ease-in-out rtl:translate-x-[var(--rtl-translate-x)] dark:border-polaris-input dark:bg-polaris-input/30"
      :class="{
        'transition-all duration-300': !disabled && !isReplyRestricted,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
